// ══════════════════════════════════════════════════════════════════════
// Runner Status Widget (Sprint 2 Phase D + Sprint 3 Phase 4)
// Zeigt den aktuellen Stand der LiveStrategyRunners aus dem Bot.
// Polled /api/runners/status alle 5s und rendert pro Runner eine Card
// mit Pipeline-Health (last_bar_processed_age_sec), Bars-Counter pro TF,
// Buffer-Zustand und Signal-Counter.
//
// Sprint 3 Phase 4: Wenn ein Trade offen ist (trade_manager_state.in_trade),
// wird unterhalb der normalen Runner-Info die TradeManagerSection gerendert
// mit Live-Trade-Details (Entry, SL, TPs, Partials, Force-Exit-Button).
// Außerdem schaltet das Polling-Interval auf 2s herunter wenn mind. 1 Trade
// offen ist (faster updates für Live-Trading-View).
// ══════════════════════════════════════════════════════════════════════

import { useApi } from "../hooks/useApi";
import { theme, fonts } from "../theme";
import { Card, SectionTitle, StatusBadge, Metric } from "./ui";
import TradeManagerSection from "./TradeManagerSection";

// Pipeline-Health Schwellen für last_bar_processed_age_sec
// M5-Bars kommen alle 5 Minuten — alles > 6 Min = stale, > 10 Min = timeout
const FRESH_SEC = 360;     // < 6 Min → grün (frische M5-Bar)
const STALE_SEC = 600;     // < 10 Min → amber (eine Bar gemissed)

// Polling-Intervalle: schneller wenn mind. 1 Trade aktiv ist
const POLL_INTERVAL_IDLE = 5000;
const POLL_INTERVAL_TRADING = 2000;

function pipelineHealth(ageSec, isRunning, warmupComplete) {
  if (!isRunning) {
    return { bg: theme.redLight, dot: theme.red, text: theme.redText, label: "STOPPED" };
  }
  if (!warmupComplete) {
    return { bg: theme.amberLight, dot: theme.amber, text: theme.amberText, label: "WARMING" };
  }
  if (ageSec === null || ageSec === undefined) {
    return { bg: theme.tint || "#f1f5f9", dot: theme.text4, text: theme.text3, label: "AWAITING DATA" };
  }
  if (ageSec < FRESH_SEC) {
    return { bg: theme.greenLight, dot: theme.green, text: theme.greenText, label: "ACTIVE" };
  }
  if (ageSec < STALE_SEC) {
    return { bg: theme.amberLight, dot: theme.amber, text: theme.amberText, label: "STALE" };
  }
  return { bg: theme.redLight, dot: theme.red, text: theme.redText, label: "TIMEOUT" };
}

function formatAge(sec) {
  if (sec === null || sec === undefined) return "—";
  if (sec < 60) return `${Math.round(sec)}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ${Math.round(sec % 60)}s`;
  return `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}m`;
}

function formatUptime(sec) {
  if (!sec || sec < 60) return `${Math.round(sec || 0)}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}m`;
  return `${Math.floor(sec / 86400)}d ${Math.floor((sec % 86400) / 3600)}h`;
}

// Helper: any runner has an active trade?
function anyTradeActive(runners) {
  for (const r of Object.values(runners || {})) {
    if (r.trade_manager_state && r.trade_manager_state.in_trade) return true;
  }
  return false;
}

export default function RunnerStatusWidget() {
  // Two-step polling: first short tick to discover state, then adapt interval.
  // Simpler approach: poll at faster interval always — but useApi gets a fixed
  // ms arg at hook-call-time. We can't easily change it mid-flight, so we just
  // use the faster 2s rate. The endpoint is cheap (just reads in-memory state).
  // Trade-off: 30% more requests when idle. Acceptable.
  const { data, error, loading } = useApi(
    "/runners/status",
    POLL_INTERVAL_TRADING,
  );

  // Loading state
  if (loading && !data) {
    return (
      <Card style={{ padding: "14px 18px", marginBottom: 16 }}>
        <div style={{ fontSize: 11, color: theme.text3, fontFamily: fonts.body }}>
          Runner status: lädt…
        </div>
      </Card>
    );
  }

  // Error state
  if (error) {
    return (
      <Card style={{ padding: "14px 18px", marginBottom: 16, borderLeft: `3px solid ${theme.red}` }}>
        <div style={{ fontSize: 11, color: theme.redText, fontFamily: fonts.body }}>
          Runner status error: {String(error)}
        </div>
      </Card>
    );
  }

  // Manager not running (no paper/live strategies)
  if (!data?.manager_running) {
    return (
      <Card style={{ padding: "14px 18px", marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ width: 7, height: 7, borderRadius: "50%", background: theme.text4 || "#94a3b8" }} />
          <span style={{ fontSize: 11, fontWeight: 700, color: theme.text3, textTransform: "uppercase", letterSpacing: "0.8px" }}>
            Runners
          </span>
          <span style={{ fontSize: 11, color: theme.text3 }}>
            inactive — no paper/live strategies configured
          </span>
        </div>
      </Card>
    );
  }

  const runners = data.runners || {};
  const runnerKeys = Object.keys(runners).sort();
  const configuredWithoutRunner = data.configured_without_runner || [];
  const tradeActive = anyTradeActive(runners);

  // Grid columns: when a trade is active, use 1 column for max visibility of TradeManagerSection
  const gridCols = tradeActive ? 1 : Math.min(runnerKeys.length, 2) || 1;

  return (
    <div style={{ marginBottom: 20 }}>
      <SectionTitle>
        Live Strategy Runners ({data.runner_count})
        {tradeActive && (
          <span style={{
            marginLeft: 8, fontSize: 10, fontWeight: 800, color: theme.greenText,
            padding: "2px 8px", borderRadius: 4, background: theme.greenLight,
            textTransform: "uppercase", letterSpacing: "0.8px",
          }}>
            ⚡ Trade Active
          </span>
        )}
        {configuredWithoutRunner.length > 0 && (
          <span style={{ marginLeft: 8, fontSize: 10, color: theme.text3, fontWeight: 500, textTransform: "none", letterSpacing: 0 }}>
            · {configuredWithoutRunner.length} test-only
          </span>
        )}
      </SectionTitle>

      {runnerKeys.length === 0 && (
        <Card style={{ padding: "14px 18px" }}>
          <div style={{ fontSize: 11, color: theme.text3 }}>
            Manager läuft, aber keine Runner registriert.
          </div>
        </Card>
      )}

      <div style={{ display: "grid", gridTemplateColumns: `repeat(${gridCols}, 1fr)`, gap: 12 }}>
        {runnerKeys.map((key) => {
          const r = runners[key] || {};
          const h = pipelineHealth(r.last_bar_processed_age_sec, r.is_running, r.warmup_complete);
          const bars = r.bars_received_by_tf || {};
          const buf = r.buffer_counts || {};
          const tmState = r.trade_manager_state;
          const inTrade = !!(tmState && tmState.in_trade);

          return (
            <Card key={key} style={{ padding: "16px 20px", borderLeft: `3px solid ${h.dot}` }}>
              {/* Header: strategy_key + status badge */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <span style={{ fontFamily: fonts.mono, fontWeight: 800, fontSize: 13, color: theme.text, letterSpacing: "-0.3px" }}>
                    {key}
                  </span>
                  <span style={{ fontSize: 10, color: theme.text3, fontFamily: fonts.body }}>
                    {r.symbol}
                  </span>
                </div>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <StatusBadge status={r.status} />
                  <span style={{
                    display: "inline-flex", alignItems: "center", gap: 5,
                    padding: "3px 9px", borderRadius: 12,
                    background: h.bg, fontSize: 9, fontWeight: 800, color: h.text,
                    letterSpacing: "0.4px",
                  }}>
                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: h.dot }} />
                    {h.label}
                  </span>
                </div>
              </div>

              {/* Bars-received pro TF */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 12, padding: "10px 12px", background: theme.tint, borderRadius: 8 }}>
                <Metric label="M5 Bars" value={bars.M5 ?? 0} small />
                <Metric label="H1 Bars" value={bars.H1 ?? 0} small />
                <Metric label="D Bars" value={bars.D ?? 0} small />
              </div>

              {/* Buffer + Signals + Uptime row */}
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 10, color: theme.text3, fontFamily: fonts.mono }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <span style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.8px", color: theme.text4 }}>
                    Buffer
                  </span>
                  <span style={{ color: theme.text2 || theme.text }}>
                    M5={buf.M5 ?? 0} · H1={buf.H1 ?? 0} · D={buf.D ?? 0}
                  </span>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <span style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.8px", color: theme.text4 }}>
                    Signals
                  </span>
                  <span style={{
                    color: (r.signals_generated || 0) > 0 ? theme.greenText : theme.text2,
                    fontWeight: (r.signals_generated || 0) > 0 ? 700 : 500,
                  }}>
                    {r.signals_generated ?? 0} fired
                  </span>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 2, textAlign: "right" }}>
                  <span style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.8px", color: theme.text4 }}>
                    Uptime
                  </span>
                  <span style={{ color: theme.text2 || theme.text }}>
                    {formatUptime(r.uptime_sec)}
                  </span>
                </div>
              </div>

              {/* Last bar info */}
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${theme.divider}`, fontSize: 10, color: theme.text3, fontFamily: fonts.mono, display: "flex", justifyContent: "space-between" }}>
                <span>
                  last bar: <strong style={{ color: theme.text2 || theme.text }}>
                    {formatAge(r.last_bar_processed_age_sec)}
                  </strong> ago
                </span>
                {r.last_signal_age_sec !== null && r.last_signal_age_sec !== undefined && (
                  <span>
                    last signal: <strong style={{ color: theme.greenText }}>
                      {formatAge(r.last_signal_age_sec)}
                    </strong> ago
                  </span>
                )}
              </div>

              {/* Sprint 3 Phase 4: Active-trade section, rendered only when in_trade */}
              {inTrade && (
                <TradeManagerSection strategyKey={key} state={tmState} />
              )}
            </Card>
          );
        })}
      </div>

      {/* Test-only strategies (no runner) */}
      {configuredWithoutRunner.length > 0 && (
        <div style={{ marginTop: 10, padding: "8px 14px", background: theme.tint, borderRadius: 8, fontSize: 10, color: theme.text3, fontFamily: fonts.mono }}>
          <span style={{ fontWeight: 700, color: theme.text4, textTransform: "uppercase", letterSpacing: "0.8px", marginRight: 8 }}>
            Test-only (no runner):
          </span>
          {configuredWithoutRunner.join(", ")}
        </div>
      )}
    </div>
  );
}
